/**
 * API code -> i18n message resolver
 * Supports module-based extension:
 * - common: shared codes across services
 * - agentcrm: module-specific override/additions
 */

import { getCodeMessage as getCodeMessageIndex } from './codeMessageIndex';

const FALLBACK_LOCALE = 'zh-CN';

const CODE_MESSAGES = {
  'zh-CN': {
    common: {
      '-1': '用户未登录或登录超时，请重新登录',
      '400': '请求参数有误，请重试',
      '404': '资源不存在',
      '429': '操作过于频繁，请稍后再试',
      '500': '服务繁忙，请您稍后重试',
      '10101': '无权限执行此操作',
      '1029': '异步导出任务提交成功',
      '1030': '导出失败，请联系客服反馈相关问题',
      '1031': '请等待您的上一条导出任务执行完毕后，再点击导出按钮',
      '100001': '员工账号非“正常”状态，不可编辑',
      '100002': '用户信息不存在{0}',
      '100003': '数据为空，请修改条件后重试',
      '100004': '系统配置异常，请稍后重试',
      '100005': '校区不存在',
      '100006': '数据映射未就绪',
      '100007': '修改失败',
      '100008': '新增失败',
      '100009': '查询失败'
    },
    agentcrm: {}
  },
  'en-US': {
    common: {
      '-1': 'User is not logged in or session has expired. Please log in again.',
      '400': 'Invalid request parameters. Please try again.',
      '404': 'Resource not found.',
      '429': 'Too many operations. Please try again later.',
      '500': 'Service is busy. Please try again later.',
      '10101': 'You do not have permission to perform this operation.',
      '1029': 'Async export task submitted successfully.',
      '1030': 'Export failed. Please contact customer service.',
      '1031': 'Please wait until your previous export task is completed, then try again.',
      '100001': 'Employee account is not in "Normal" status and cannot be edited.',
      '100002': 'User information does not exist {0}.',
      '100003': 'No data. Please adjust conditions and try again.',
      '100004': 'System configuration exception. Please try again later.',
      '100005': 'Campus does not exist.',
      '100006': 'Data mapping is not ready.',
      '100007': 'Update failed.',
      '100008': 'Create failed.',
      '100009': 'Query failed.'
    },
    agentcrm: {}
  }
};

function getLocale(locale) {
  if (locale && CODE_MESSAGES[locale]) return locale;
  return FALLBACK_LOCALE;
}

function normalizeCode(code) {
  if (code === null || code === undefined) return '';
  return String(code);
}

/**
 * Resolve API message by code and locale.
 * @param {Object} payload
 * @param {number|string} payload.code
 * @param {string} payload.backendMessage
 * @param {string} payload.locale
 * @param {string} payload.module - e.g. 'agentcrm'
 * @returns {string}
 */
export function resolveApiCodeMessage({
  code,
  backendMessage = '',
  locale = FALLBACK_LOCALE,
  module = 'common'
}) {
  // 为避免与 `src/i18n/code-messages/**`/`src/i18n/code-index/**` 数据源不一致
  // 这里统一委托给 `codeMessageIndex`，保证 `this.$getCodeMessage` 与
  // axios 拦截器归一化使用同一套映射表。
  return getCodeMessageIndex(code, locale, backendMessage || '');
}

/**
 * Register/merge code-message map for future module expansion.
 * @param {string} locale - 'zh-CN' | 'en-US' | other
 * @param {string} module
 * @param {Object} messages - { code: message }
 */
export function registerApiCodeMessages(locale, module, messages) {
  if (!locale || !module || !messages || typeof messages !== 'object') return;
  if (!CODE_MESSAGES[locale]) CODE_MESSAGES[locale] = { common: {} };
  if (!CODE_MESSAGES[locale][module]) CODE_MESSAGES[locale][module] = {};
  Object.assign(CODE_MESSAGES[locale][module], messages);
}

export default resolveApiCodeMessage;
